import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  CardActionArea,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  PersonRemove as PersonRemoveIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import LinkStudentDialog from '../../components/teacher/LinkStudentDialog';
import InvitationLinkCard from '../../components/teacher/InvitationLinkCard';
import InvitationManagementDialog from '../../components/teacher/InvitationManagementDialog';
import { useAuth } from '../../contexts/AuthContext';
import { getStudents, getStudentProgress, removeStudent, generateInvitationLink } from '../../services/teacherService';
import { getRankedRoster } from '../../services/rankedService';
import { RankedRosterEntry } from '../../types/ranked';
import RankedSummaryStrip from '../../components/ranked/RankedSummaryStrip';
import { Student, StudentProgress } from '../../types/teacher';

interface StudentWithProgress {
  student: Student;
  progress: StudentProgress | null;
  loading: boolean;
}

function TeacherDashboardPage() {
  const { authState } = useAuth();
  const { user } = authState;
  const navigate = useNavigate();

  const [students, setStudents] = useState<StudentWithProgress[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState<boolean>(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState<boolean>(false);
  const [studentToRemove, setStudentToRemove] = useState<Student | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [invitationRefreshTrigger, setInvitationRefreshTrigger] = useState<number>(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [generatedLinkDialogOpen, setGeneratedLinkDialogOpen] = useState<boolean>(false);
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [roster, setRoster] = useState<Map<number, RankedRosterEntry>>(new Map());
  const [totalRanked, setTotalRanked] = useState<number>(0);
  const [rosterLoading, setRosterLoading] = useState<boolean>(true);

  // Fetch students and their progress
  useEffect(() => {
    const fetchStudentsData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all students
        const studentsData = await getStudents();

        // Initialize students with loading state
        const studentsWithProgress: StudentWithProgress[] = studentsData.map(student => ({
          student,
          progress: null,
          loading: true
        }));

        setStudents(studentsWithProgress);

        // Fetch progress for each student
        const progressPromises = studentsData.map(async (student, index) => {
          try {
            const progressData = await getStudentProgress(student.id);
            return { index, progressData };
          } catch (err) {
            console.error(`Error fetching progress for student ${student.id}:`, err);
            return { index, progressData: null };
          }
        });

        const progressResults = await Promise.all(progressPromises);

        // Ranked standing for the whole roster in ONE request, caught
        // separately so a ranked failure leaves the roster itself intact.
        try {
          const rankedRoster = await getRankedRoster(studentsData.map(s => s.id));
          setRoster(new Map(rankedRoster.entries.map(entry => [entry.userId, entry])));
          setTotalRanked(rankedRoster.totalRanked);
        } catch (err) {
          console.error('Error fetching ranked roster:', err);
        } finally {
          setRosterLoading(false);
        }

        // Update state with progress data
        setStudents(prev => {
          const updated = [...prev];
          progressResults.forEach(({ index, progressData }) => {
            updated[index] = {
              ...updated[index],
              progress: progressData,
              loading: false
            };
          });
          return updated;
        });
      } catch (err) {
        console.error('Error fetching students:', err);
        setError(err instanceof Error ? err.message : 'Failed to load students data');
      } finally {
        setLoading(false);
      }
    };

    fetchStudentsData();
  }, []);

  // Handle successful student link - refresh the students list
  const handleLinkSuccess = async () => {
    setLinkDialogOpen(false);

    try {
      setLoading(true);
      setError(null);

      const studentsData = await getStudents();
      const studentsWithProgress: StudentWithProgress[] = studentsData.map(student => ({
        student,
        progress: null,
        loading: true
      }));

      setStudents(studentsWithProgress);

      const progressPromises = studentsData.map(async (student, index) => {
        try {
          const progressData = await getStudentProgress(student.id);
          return { index, progressData };
        } catch (err) {
          console.error(`Error fetching progress for student ${student.id}:`, err);
          return { index, progressData: null };
        }
      });

      const progressResults = await Promise.all(progressPromises);

      setStudents(prev => {
        const updated = [...prev];
        progressResults.forEach(({ index, progressData }) => {
          updated[index] = {
            ...updated[index],
            progress: progressData,
            loading: false
          };
        });
        return updated;
      });
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(err instanceof Error ? err.message : 'Failed to load students data');
    } finally {
      setLoading(false);
    }
  };

  // Handle opening remove dialog
  const handleOpenRemoveDialog = (student: Student, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click navigation
    setStudentToRemove(student);
    setRemoveDialogOpen(true);
  };

  // Handle closing remove dialog
  const handleCloseRemoveDialog = () => {
    setRemoveDialogOpen(false);
    setStudentToRemove(null);
  };

  // Handle confirming student removal
  const handleConfirmRemove = async () => {
    if (!studentToRemove) return;

    try {
      setRemoving(true);
      setError(null);

      await removeStudent(studentToRemove.id);

      // Remove the student from local state
      setStudents(prev => prev.filter(s => s.student.id !== studentToRemove.id));

      handleCloseRemoveDialog();
    } catch (err) {
      console.error('Error removing student:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove student');
    } finally {
      setRemoving(false);
    }
  };

  // Handle generating a new invitation link
  const handleGenerateLink = async () => {
    try {
      setGenerating(true);
      setError(null);

      const newToken = await generateInvitationLink();
      const url = `${window.location.origin}/register?invite=${newToken.token}`;

      // Trigger refresh of invitation card
      setInvitationRefreshTrigger(prev => prev + 1);

      // Show the generated link in a dialog
      setGeneratedLink(url);
      setGeneratedLinkDialogOpen(true);
    } catch (err) {
      console.error('Error generating invitation link:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate invitation link');
    } finally {
      setGenerating(false);
    }
  };

  // Handle copying the generated link
  const handleCopyGeneratedLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Handle closing the generated link dialog
  const handleCloseGeneratedLinkDialog = () => {
    setGeneratedLinkDialogOpen(false);
    setLinkCopied(false);
  };

  // Handle closing the invitation management dialog
  const handleInvitationDialogClose = () => {
    setInvitationDialogOpen(false);
    // Trigger refresh of invitation card
    setInvitationRefreshTrigger(prev => prev + 1);
  };

  // Calculate aggregate stats
  const aggregateStats = students.reduce(
    (acc, { progress }) => {
      if (progress) {
        acc.totalSubmissions += progress.totalSubmissions;
        acc.totalCorrect += progress.correctSubmissions;
        acc.totalPoints += progress.totalPoints;
        acc.totalAchievements += progress.achievementsEarned;
      }
      return acc;
    },
    { totalSubmissions: 0, totalCorrect: 0, totalPoints: 0, totalAchievements: 0 }
  );

  const overallAccuracy = aggregateStats.totalSubmissions > 0
    ? Math.round((aggregateStats.totalCorrect / aggregateStats.totalSubmissions) * 100)
    : 0;

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          data-testid="welcome-message"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            letterSpacing: '-0.01em',
            fontSize: '1.5rem',
            lineHeight: 1.3
          }}
        >
          Welcome, {user?.firstName}!
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: '0.95rem',
            maxWidth: '90%',
            mb: 2
          }}
        >
          Manage your students and track their progress.
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column - Student List */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} data-demo-id="teacher-roster-card">
            <CardHeader
              title="My Students"
              action={
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={generating ? <CircularProgress size={16} /> : <LinkIcon />}
                    size="small"
                    onClick={handleGenerateLink}
                    disabled={generating}
                    data-testid="generate-link-button"
                  >
                    {generating ? 'Generating...' : 'Generate Invitation Link'}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setLinkDialogOpen(true)}
                    data-testid="add-student-button"
                  >
                    Add Student
                  </Button>
                </Box>
              }
            />
            <Divider />
            <CardContent>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : students.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No students yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Click "Add Student" to start building your class roster.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {students.map(({ student, progress, loading: progressLoading }) => (
                    <Grid item xs={12} key={student.id}>
                      <Card
                        elevation={1}
                        sx={{
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)'
                          },
                          position: 'relative'
                        }}
                      >
                        {/* Remove button - positioned absolutely */}
                        <Tooltip title="Remove student">
                          <IconButton
                            onClick={(e) => handleOpenRemoveDialog(student, e)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              '&:hover': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText'
                              }
                            }}
                            size="small"
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <CardActionArea
                          onClick={() => navigate(`/teacher/students/${student.id}`)}
                          data-testid={`student-card-${student.id}`}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 5 }}>
                              <Avatar
                                sx={{
                                  width: 56,
                                  height: 56,
                                  mr: 2,
                                  bgcolor: 'success.main',
                                  fontSize: '1.25rem'
                                }}
                              >
                                {student.firstName?.charAt(0)}{student.lastName?.charAt(0)}
                              </Avatar>

                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="h6" fontWeight="600" noWrap>
                                    {student.firstName} {student.lastName}
                                  </Typography>
                                  <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </Box>
                                {progress && (
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                    {progress.grade && (
                                      <Chip
                                        size="small"
                                        label={`Grade ${progress.grade}`}
                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                      />
                                    )}
                                    {progress.school && (
                                      <Chip
                                        size="small"
                                        label={progress.school}
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>

                              {/* Quick Stats Summary */}
                              {progressLoading ? (
                                <CircularProgress size={24} sx={{ ml: 2 }} />
                              ) : progress ? (
                                <Box sx={{ display: 'flex', gap: 4, ml: 2 }}>
                                  <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                                    <Typography variant="h5" fontWeight="bold" color="primary">
                                      {progress.totalSubmissions}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Problems
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                                    <Typography variant="h5" fontWeight="bold" color="success.main">
                                      {progress.accuracyPercentage.toFixed(0)}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Accuracy
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                                    <Typography variant="h5" fontWeight="bold" color="secondary.main">
                                      {progress.totalPoints.toFixed(0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Points
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : null}
                            </Box>

                            {/* Ranked standing. The compact form only — the
                                full ladder lives on /teacher/ranked. */}
                            <Box sx={{ mt: 1.5 }} data-testid={`student-ranked-${student.id}`}>
                              <RankedSummaryStrip
                                entry={roster.get(student.id) ?? null}
                                totalRanked={totalRanked}
                                loading={rosterLoading}
                              />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary Stats */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Class Statistics" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.success.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <PersonIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{students.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Students</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.primary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{aggregateStats.totalSubmissions}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Problems</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.secondary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'secondary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{overallAccuracy}%</Typography>
                    <Typography variant="body2" color="text.secondary">Class Accuracy</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.warning.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrophyIcon sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{aggregateStats.totalAchievements}</Typography>
                    <Typography variant="body2" color="text.secondary">Achievements</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader title="Quick Actions" />
            <Divider />
            <CardContent>
              <Typography variant="body2" paragraph color="text.secondary">
                Manage your class roster and track student performance.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => setLinkDialogOpen(true)}
                  sx={{ mb: 1 }}
                  data-testid="quick-add-student-button"
                >
                  Add Student
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  sx={{ mb: 1 }}
                >
                  View Class Reports
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                >
                  Create Assignment
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Invitation Link Card */}
          <Box sx={{ mt: 4 }}>
            <InvitationLinkCard refreshTrigger={invitationRefreshTrigger} />
          </Box>
        </Grid>

      </Grid>

      {/* Link Student Dialog */}
      <LinkStudentDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSuccess={handleLinkSuccess}
      />

      {/* Invitation Management Dialog */}
      <InvitationManagementDialog
        open={invitationDialogOpen}
        onClose={handleInvitationDialogClose}
      />

      {/* Remove Student Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCloseRemoveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Student</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>
              {studentToRemove?.firstName} {studentToRemove?.lastName}
            </strong>{' '}
            from your class?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This will unlink the student from your account. Their account and progress data will
            not be deleted, but you will no longer have access to view their information.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDialog} disabled={removing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="error"
            variant="contained"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={20} /> : <PersonRemoveIcon />}
          >
            {removing ? 'Removing...' : 'Remove Student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated Link Dialog */}
      <Dialog
        open={generatedLinkDialogOpen}
        onClose={handleCloseGeneratedLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invitation Link Generated!</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this link with your students. They can use it to register a new account or link their existing account to your class.
          </DialogContentText>
          <TextField
            fullWidth
            value={generatedLink}
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={linkCopied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton onClick={handleCopyGeneratedLink} edge="end">
                      {linkCopied ? <CheckCircleIcon color="success" /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGeneratedLinkDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </MainLayout>
  );
}

export default TeacherDashboardPage;
